import { Alert, AlertTitle } from "@mui/material";
import React from "react";
import { CmpInvoicePaymentSurfaceStatusEnum } from "../../../graphql/types";
import { formatCurrency } from "../Invoicing/invoiceFormatting";
import { STAFF_SUPPORT_EMAIL } from "../../../utils/userFacingError";

export type MemberPastTransitionInvoiceEvidence = {
  id?: string | null;
  invoiceNumber?: string | null;
  cmpPastTransitionPaymentSurfaceStatus?: CmpInvoicePaymentSurfaceStatusEnum | null;
  cmpPastTransitionPaymentSurfaceDetail?: string | null;
};

export type MemberPastTransitionRefundEvidenceInput = {
  refundRequired?: boolean | null;
  refundRequiredAmountInCents?: number | null;
  refundRequiredInvoiceId?: string | null;
};

export type MemberPastTransitionRefundEvidenceState = {
  amountInCents: number | null;
  invoiceId: string | null;
  invoiceNumber: string | null;
};

export type MemberPastTransitionPaymentSurfaceFeedbackState = {
  severity: "success" | "info" | "warning";
  title: string;
  detail: string;
};

const getDistinctDetails = (
  invoices: ReadonlyArray<MemberPastTransitionInvoiceEvidence>,
  status: CmpInvoicePaymentSurfaceStatusEnum
) =>
  Array.from(
    new Set(
      invoices
        .filter(
          (invoice) =>
            invoice.cmpPastTransitionPaymentSurfaceStatus === status
        )
        .map((invoice) =>
          invoice.cmpPastTransitionPaymentSurfaceDetail?.trim()
        )
        .filter((detail): detail is string => Boolean(detail))
    )
  );

/**
 * Reduces the application-invoice chain to one staff-visible outcome. Manual
 * reconciliation always wins over an in-progress or completed retirement;
 * an in-progress retirement wins over a completed one. Backend detail is
 * fixed, user-safe guidance and React renders it as text.
 */
export const getMemberPastTransitionPaymentSurfaceFeedback = (
  invoices: ReadonlyArray<MemberPastTransitionInvoiceEvidence> | null | undefined
): MemberPastTransitionPaymentSurfaceFeedbackState | null => {
  const paymentSurfaceInvoices = (invoices || []).filter(
    (invoice) => invoice.cmpPastTransitionPaymentSurfaceStatus
  );

  if (paymentSurfaceInvoices.length === 0) {
    return null;
  }

  if (
    paymentSurfaceInvoices.some(
      (invoice) =>
        invoice.cmpPastTransitionPaymentSurfaceStatus ===
        CmpInvoicePaymentSurfaceStatusEnum.ReconciliationRequired
    )
  ) {
    const details = getDistinctDetails(
      paymentSurfaceInvoices,
      CmpInvoicePaymentSurfaceStatusEnum.ReconciliationRequired
    );

    return {
      severity: "warning",
      title: "A payment needs a check",
      // The server detail (cmpPastTransitionPaymentSurfaceDetail) shows first.
      detail:
        details.join(" ") ||
        `We could not close an old payment link for this member. Do not send a new invoice yet. Email ${STAFF_SUPPORT_EMAIL} for help.`,
    };
  }

  if (
    paymentSurfaceInvoices.some(
      (invoice) =>
        invoice.cmpPastTransitionPaymentSurfaceStatus ===
        CmpInvoicePaymentSurfaceStatusEnum.Pending
    )
  ) {
    return {
      severity: "info",
      title: "Closing old payment links",
      detail:
        "The member is now Past. We are still closing the old payment links. Refresh the page in a few minutes before you send a new invoice.",
    };
  }

  return {
    severity: "success",
    title: "Payment links closed",
    detail: "No old membership payment links are open for this member.",
  };
};

/**
 * Past-member refund evidence is durable accounting state, independent from
 * provider-surface cleanup. A positive amount wins over a stale false flag so
 * staff never lose visible support follow-up after a reload.
 */
export const getMemberPastTransitionRefundEvidence = ({
  invoices,
  memberExtension,
}: {
  invoices: ReadonlyArray<MemberPastTransitionInvoiceEvidence> | null | undefined;
  memberExtension: MemberPastTransitionRefundEvidenceInput | null | undefined;
}): MemberPastTransitionRefundEvidenceState | null => {
  const rawAmount = memberExtension?.refundRequiredAmountInCents;
  const amountInCents =
    typeof rawAmount === "number" && Number.isFinite(rawAmount) && rawAmount > 0
      ? rawAmount
      : null;

  if (!memberExtension?.refundRequired && amountInCents === null) {
    return null;
  }

  const invoiceId = memberExtension?.refundRequiredInvoiceId?.trim() || null;
  const invoice = invoiceId
    ? (invoices || []).find((candidate) => candidate.id === invoiceId)
    : null;

  return {
    amountInCents,
    invoiceId,
    invoiceNumber: invoice?.invoiceNumber?.trim() || null,
  };
};

export type MemberPastTransitionRefundEvidenceProps = {
  evidence: MemberPastTransitionRefundEvidenceState | null;
  className?: string;
  // Status-model chambers show "Deleted" until the member becomes Past.
  statusLabel?: "Deleted" | "Past";
};

export const MemberPastTransitionRefundEvidence = ({
  className,
  evidence,
  statusLabel = "Past",
}: MemberPastTransitionRefundEvidenceProps) => {
  if (!evidence) {
    return null;
  }

  const amountDescription =
    evidence.amountInCents === null
      ? `This ${statusLabel} member has a payment that needs a refund check.`
      : `This ${statusLabel} member paid ${formatCurrency(evidence.amountInCents)}.`;
  // Staff see the invoice number only. The internal invoice id is never shown.
  const invoiceDescription = evidence.invoiceNumber
    ? ` Open invoice ${evidence.invoiceNumber} in Invoices & Payments > Manage all Invoices & Payments.`
    : " Open the membership invoices for this member in Invoices & Payments > Manage all Invoices & Payments.";

  return (
    <Alert severity="warning" className={className}>
      <AlertTitle>Refund follow-up required</AlertTitle>
      {amountDescription}
      {invoiceDescription} To refund the payment, use Actions &gt;
      Credit/Refund Invoice. This member stays {statusLabel}.
    </Alert>
  );
};

export type MemberPastTransitionPaymentSurfaceFeedbackProps = {
  feedback: MemberPastTransitionPaymentSurfaceFeedbackState | null;
  className?: string;
};

const MemberPastTransitionPaymentSurfaceFeedback = ({
  className,
  feedback,
}: MemberPastTransitionPaymentSurfaceFeedbackProps) => {
  if (!feedback) return null;
  // A completed cleanup is part of the action's success pop-up, so it is
  // plain text there. Pending and reconciliation states stay as alerts.
  if (feedback.severity === "success") {
    return (
      <p className={`text-[14px] leading-[1.55] text-[#0A0A0A] ${className || ""}`}>
        {feedback.title}. {feedback.detail}
      </p>
    );
  }
  return (
    <Alert severity={feedback.severity} className={className}>
      <AlertTitle>{feedback.title}</AlertTitle>
      {feedback.detail}
    </Alert>
  );
};

export default MemberPastTransitionPaymentSurfaceFeedback;
